import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Row, Collapse } from 'reactstrap';
import { QuantityDeliverySection } from '../../../../../../Components/QuantityTable';
import { PortRestrictionsTable } from '../../../../../../Components/PortRestriction';
import {
  displayValue,
  formatProposalDeadline,
  getContractPeriodProps,
  getContractTypeLabel,
  getPaymentCurrencyLabel,
} from '../../../../utils/tenderEvaluationHelpers';
import { getVisibleProcurementClauses } from '../../../../../../shared/offer-domain/respond';
import { formatBuyerDelayPenaltyAmount } from '../../../../../../shared/offer-domain/payment';
import {
  getProcurementProductTypeNames,
  hasMultipleProductTypes,
  isLpgProcurement,
} from '../../../../../../shared/offer-domain/constants';
import { api } from '../../../../../../config';
import { EmptyState } from '../../../../../../Components/Common/ListStates';
import { SkeletonCard } from '../../../../../../Components/Common/Skeleton';
import { getAuthToken } from '../../../../../../auth/tokenvalidation';
import '../../../../../../App.css';

const InfoBlock = ({ label, value, col = 3, isHtml = false }) => (
  <Col xs={6} md={col}>
    <div className='border-start border-2 border-primary ps-3'>
      <div className='text-muted small fw-semibold mb-2'>{label}</div>
      {isHtml ? (
        <HtmlContent value={value} />
      ) : (
        <div className='text-dark fs-13 mb-0 lh-base'>
          {displayValue(value)}
        </div>
      )}
    </div>
  </Col>
);

const MandatoryBadge = () => (
  <span className='badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill fw-medium fs-11'>
    Mandatory
  </span>
);

const HtmlContent = ({ value, framed = true }) => {
  if (!value) {
    return <div className='text-dark mb-0 lh-base'>-</div>;
  }

  const body = (
    <div
      className='html-content-body'
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );

  if (!framed) return body;

  return <div className='html-content-panel'>{body}</div>;
};

const ClauseBlock = ({ index, title, value, isMandatory }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={`rounded-3 overflow-hidden mb-2 ${
        isExpanded
          ? 'border border-primary shadow-sm bg-white'
          : 'border bg-white'
      }`}
    >
      <button
        type='button'
        className='w-100 border-0 bg-transparent text-start px-3 py-3 d-flex align-items-center gap-3'
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
      >
        <span
          className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold fs-12 ${
            isExpanded ? 'bg-primary text-white' : 'bg-light text-muted border'
          }`}
          style={{ width: '28px', height: '28px' }}
        >
          {index}
        </span>

        <div className='flex-grow-1' style={{ minWidth: 0 }}>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <span
              className={`fs-14 mb-0 ${
                isExpanded ? 'fw-bold text-dark' : 'fw-semibold text-dark'
              }`}
            >
              {title}
            </span>
            {isMandatory && <MandatoryBadge />}
          </div>
          {!isExpanded && (
            <span className='text-muted fs-12'>
              Click to view clause content
            </span>
          )}
        </div>

        <span
          className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
            isExpanded
              ? 'bg-primary-subtle text-primary'
              : 'bg-light text-muted'
          }`}
          style={{ width: '28px', height: '28px' }}
        >
          <i
            className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
          ></i>
        </span>
      </button>

      <Collapse isOpen={isExpanded}>
        <div className='px-3 pb-3'>
          <div className='ms-1 ps-4'>
            {isMandatory && (
              <div className='text-danger small fw-medium mb-2'>
                * Mandatory clause for DMUT to fulfill
              </div>
            )}
            <div className='p-3 rounded-3 bg-light-subtle border text-muted fs-13 lh-base'>
              <HtmlContent value={value} framed={false} />
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

const RequirementCard = ({ procurement, parcels, isLoadingParcels }) => {
  const periodProps = getContractPeriodProps(procurement);

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <Row>
          <Col xs={12}>
            <div className='mb-4'>
              <h5 className='fw-bold text-dark mb-1'>Requirement</h5>
              <p className='text-muted small'>
                Contract and product requirements for this procurement.
              </p>
            </div>

            <hr className='my-2 border-secondary' />

            <Row className='g-3'>
              <Col xs={12}>
                <div className='mb-4'>
                  <h5 className='fw-bold text-dark mb-1'>Contract</h5>
                </div>
              </Col>
              <InfoBlock
                label='Contract Type'
                value={getContractTypeLabel(procurement?.contractType)}
              />
              <InfoBlock
                label={periodProps.startLabel}
                value={periodProps.startValue}
                col={3}
              />
              {periodProps.endLabel && (
                <InfoBlock
                  label={periodProps.endLabel}
                  value={periodProps.endValue}
                  col={3}
                />
              )}
              <InfoBlock
                label='Procurement No'
                value={procurement?.procurementNumber}
                col={3}
              />
            </Row>
          </Col>

          <hr className='my-4 border-secondary border-dashed' />

          <Col xs={12}>
            <Row className='g-3'>
              <Col xs={12}>
                <div className='mb-4'>
                  <h5 className='fw-bold text-dark mb-1'>
                    Product Specification/ Quantity
                  </h5>
                  <p className='text-muted small'>
                    Bisa jadi Kalimat tambahan untuk membantu section mengenai
                    apa ini .
                  </p>
                </div>
              </Col>
              <InfoBlock
                label='Product Name'
                value={procurement?.productType?.product?.name}
              />
              <InfoBlock
                label='Product Type'
                value={
                  hasMultipleProductTypes(procurement)
                    ? getProcurementProductTypeNames(procurement)
                    : procurement?.productType?.description
                }
              />
              <InfoBlock
                label='Operational Tolerance'
                value={procurement?.operationalTolerance + '%'}
              />
              <InfoBlock
                label='Operation Tolerance Options'
                value={procurement?.partyType}
              />
              <InfoBlock
                label='Product Specification'
                value={procurement?.productSpecification}
                col={12}
                isHtml
              />
            </Row>
          </Col>

          <hr className='my-4 border-secondary border-dashed' />

          <Col xs={12}>
            <div className='mb-4'>
              <h5 className='fw-bold text-dark mb-1'>
                Quantity & Delivery Time
              </h5>
            </div>

            <QuantityDeliverySection
              parcels={parcels}
              isLoading={isLoadingParcels}
              isLpg={isLpgProcurement(procurement)}
              quantityUnitLabel={procurement?.productType?.unit}
            />

            <hr className='my-4 border-secondary border-dashed' />

            <Row className='mt-2 g-3'>
              <Col md={12}>
                <div className='border-start border-2 border-primary ps-3'>
                  <div className='text-muted small fw-semibold mb-2'>Notes</div>
                  <HtmlContent value={procurement?.globalNotes} />
                </div>
              </Col>
              <hr className='my-4 border-secondary border-dashed' />
              <Col md={12}>
                <div className='mt-2'>
                  <h5 className='fw-bold text-dark mb-3'>Port Restrictions</h5>
                  <PortRestrictionsTable
                    ports={procurement?.portRestriction}
                    procurementId={procurement?.id}
                  />
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const ClauseDetailsCard = ({ procurement }) => {
  const clauses = getVisibleProcurementClauses(procurement);

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4'>
          <div>
            <h5 className='fw-bold text-dark mb-1'>Clause Details</h5>
            <p className='text-muted small mb-0'>
              Review procurement clauses and requirements for this tender
            </p>
          </div>
          <span className='badge bg-primary-subtle text-primary rounded-pill fw-medium fs-12 flex-shrink-0'>
            {clauses.length} clauses
          </span>
        </div>

        {clauses.length > 0 ? (
          <div className='d-flex flex-column'>
            {clauses.map((clause, index) => (
              <ClauseBlock
                key={clause.id}
                index={index + 1}
                title={clause.clauseTitle}
                value={clause.clauseDescription}
                isMandatory={clause.isShowToDMUT === true}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            compact
            icon='ri-file-list-3-line'
            title='No clauses'
            emptyHint='This procurement has no clauses visible to bidders.'
          />
        )}
      </CardBody>
    </Card>
  );
};

const PaymentComplianceCard = ({ procurement }) => {
  const [invoiceQuantityLabel, setInvoiceQuantityLabel] = useState(null);
  const [modeLabel, setModeLabel] = useState(null);

  useEffect(() => {
    const token = getAuthToken();
    const invoicingQuantityId = procurement?.invoicingQuantityId;
    const modeInvoicingQuantityId = procurement?.modeInvoicingQuantityId;
    let cancelled = false;

    const resolveLabels = async () => {
      if (!token) return;

      if (!procurement?.invoicingQuantity?.name && invoicingQuantityId) {
        try {
          const response = await fetch(`${api.API_URL}/invoicing-quantity`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });
          const result = await response.json();
          const items = result?.data?.items || result?.data || [];
          const match = (Array.isArray(items) ? items : []).find(
            item => String(item?.id) === String(invoicingQuantityId)
          );
          if (!cancelled) setInvoiceQuantityLabel(match?.name ?? null);
        } catch {
          if (!cancelled) setInvoiceQuantityLabel(null);
        }
      } else if (!cancelled) {
        setInvoiceQuantityLabel(null);
      }

      if (
        !procurement?.modeInvoicingQuantity?.name &&
        invoicingQuantityId &&
        modeInvoicingQuantityId
      ) {
        try {
          const response = await fetch(
            `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity?InvoicingQuantityId=${invoicingQuantityId}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const result = await response.json();
          const items = result?.data?.items || result?.data || [];
          const match = (Array.isArray(items) ? items : []).find(
            item => String(item?.id) === String(modeInvoicingQuantityId)
          );
          if (!cancelled) setModeLabel(match?.name ?? null);
        } catch {
          if (!cancelled) setModeLabel(null);
        }
      } else if (!cancelled) {
        setModeLabel(null);
      }
    };

    resolveLabels();
    return () => {
      cancelled = true;
    };
  }, [
    procurement?.invoicingQuantity?.name,
    procurement?.invoicingQuantityId,
    procurement?.modeInvoicingQuantity?.name,
    procurement?.modeInvoicingQuantityId,
  ]);

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <Row>
          <Col xs={12}>
            <div className='mb-4'>
              <h5 className='fw-bold text-dark mb-1'>Delay Penalty Terms</h5>
              <p className='text-muted small'>
                Penalty that applies when delivery is late.
              </p>

              <Row className='g-3'>
                <InfoBlock
                  label='Delay Penalty Amount'
                  value={formatBuyerDelayPenaltyAmount(
                    procurement?.delayPenaltyAmount
                  )}
                />
                <Col xs={12}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-2'>
                      Description of Delay Penalty Terms
                    </div>
                    <HtmlContent
                      value={procurement?.descriptionOfDelayPenaltyTerms}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </Col>

          <hr className='my-4 border-secondary border-dashed' />

          <Col xs={12}>
            <div className='mb-4'>
              <h5 className='fw-bold text-dark mb-1'>Payment Terms</h5>
              <p className='text-muted small'>
                Bisa jadi Kalimat tambahan untuk membantu section mengenai apa
                ini
              </p>

              <Row className='g-3'>
                <InfoBlock
                  label='Invoicing Quantity'
                  value={
                    procurement?.invoicingQuantity?.name || invoiceQuantityLabel
                  }
                />
                <InfoBlock
                  label='Mode'
                  value={procurement?.modeInvoicingQuantity?.name || modeLabel}
                />
                <InfoBlock
                  label='Currency'
                  value={getPaymentCurrencyLabel(procurement?.paymentCurrency)}
                />
                <Col xs={12}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-2'>
                      Description of Payment Term
                    </div>
                    <HtmlContent
                      value={procurement?.descriptionOfPaymentTerms}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </Col>

          <hr className='my-4 border-secondary border-dashed' />

          <Col xs={12}>
            <div className='mb-4'>
              <h5 className='fw-bold text-dark mb-1'>Compliance</h5>
              <p className='text-muted small'>
                Bisa jadi Kalimat tambahan untuk membantu section mengenai apa
                ini
              </p>

              <Row className='g-3'>
                <InfoBlock label='Law' value={procurement?.law?.name} col={6} />
                <InfoBlock
                  label='Arbitration'
                  value={procurement?.arbitration?.name}
                  col={6}
                />
                <Col xs={12}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-2'>
                      Other Terms & Condition
                    </div>
                    <HtmlContent value={procurement?.otherTermsAndCondition} />
                  </div>
                </Col>
                <Col xs={12}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-2'>
                      Additional Notes
                    </div>
                    <HtmlContent value={procurement?.additionalNotes} />
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const ValidityCard = ({ procurement }) => (
  <Card className='border shadow-md mb-4'>
    <CardBody>
      <Row>
        <Col xs={12}>
          <div>
            <h5 className='fw-bold text-dark mb-1'>Validity</h5>
            <p className='text-muted small'>
              Bisa jadi Kalimat tambahan untuk membantu section mengenai apa ini
            </p>

            <Row className='g-3'>
              <InfoBlock
                label='Submission Deadline (Jakarta Local Time)'
                value={formatProposalDeadline(
                  procurement?.proposalSubmissionDeadline
                )}
                col={6}
              />
              <InfoBlock
                label='Validity Period (Jakarta Local Time)'
                value={formatProposalDeadline(
                  procurement?.proposalValidityPeriodMax
                )}
                col={6}
              />
            </Row>
          </div>
        </Col>
      </Row>
    </CardBody>
  </Card>
);

const TabRequirement = ({
  procurement,
  parcels = [],
  isLoadingProcurement,
  isLoadingParcels,
}) => {
  if (isLoadingProcurement && !procurement) {
    return (
      <>
        <SkeletonCard lines={5} className='mb-4' />
        <SkeletonCard lines={4} className='mb-4' />
        <SkeletonCard lines={3} />
      </>
    );
  }

  return (
    <React.Fragment>
      <RequirementCard
        procurement={procurement}
        parcels={parcels}
        isLoadingParcels={isLoadingParcels}
      />

      <ClauseDetailsCard procurement={procurement} />

      <PaymentComplianceCard procurement={procurement} />

      <ValidityCard procurement={procurement} />
    </React.Fragment>
  );
};

export default TabRequirement;
